// hooks/useTheme.ts
// Injecte dynamiquement les couleurs de la charte graphique de l'entité
// via des variables CSS sur :root, utilisables partout dans l'app.
import { useEffect } from 'react';
import { User } from '../types/user';

/** Couleurs par défaut de SAGeCOM (fallback si l'entité n'a pas de charte) */
const DEFAULTS = {
  primary:   '#1a56db',   // bleu
  secondary: '#6B7280',   // gris
  accent:    '#7c3aed',   // violet
};

/** Convertit une couleur hex en composantes R G B séparées par des espaces (pour Tailwind/CSS) */
function hexToRgbParts(hex: string): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '26 86 219';
  return `${r} ${g} ${b}`;
}

/** Génère une version "claire" d'une couleur (mélange avec blanc à 85%) pour les fonds */
function lighten(hex: string, ratio = 0.88): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = Math.round(parseInt(full.substring(0, 2), 16) * (1 - ratio) + 255 * ratio);
  const g = Math.round(parseInt(full.substring(2, 4), 16) * (1 - ratio) + 255 * ratio);
  const b = Math.round(parseInt(full.substring(4, 6), 16) * (1 - ratio) + 255 * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Luminance relative WCAG : au-dessous de 0,45 le fond appelle un texte clair. */
function estSombre(hex: string): boolean {
  const h = hex.replace('#', '');
  const plein = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const canaux = [0, 2, 4].map(i => parseInt(plein.substring(i, i + 2), 16) / 255);
  const lin = canaux.map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2] < 0.45;
}

/** Luminance relative WCAG d'une couleur hexadecimale. */
function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const plein = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const canaux = [0, 2, 4].map(i => parseInt(plein.substring(i, i + 2), 16) / 255);
  const lin = canaux.map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** Version attenuee d'une couleur, pour les libelles secondaires. */
function attenuer(hex: string, opacite: number): string {
  const h = hex.replace('#', '');
  const plein = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map(i => parseInt(plein.substring(i, i + 2), 16));
  if ([r, g, b].some(Number.isNaN)) return `rgba(17,24,39,${opacite})`;
  return `rgba(${r}, ${g}, ${b}, ${opacite})`;
}

/** Du quasi-noir ou du blanc, celui qui contraste le plus avec `hex`. */
function contrasteMax(hex: string): string {
  const l = luminance(hex);
  const contrasteNoir = (l + 0.05) / (luminance('#111827') + 0.05);
  const contrasteBlanc = (1.0 + 0.05) / (l + 0.05);
  return contrasteNoir >= contrasteBlanc ? '#111827' : '#ffffff';
}

export function useTheme(user: User | null): void {
  useEffect(() => {
    const root = document.documentElement;

    const primary   = user?.couleurPrimaire   || DEFAULTS.primary;
    const secondary = user?.couleurSecondaire || DEFAULTS.secondary;
    const accent    = user?.couleurAccent     || DEFAULTS.accent;

    // Variables CSS principales
    root.style.setProperty('--color-primary',        primary);
    root.style.setProperty('--color-primary-rgb',    hexToRgbParts(primary));
    root.style.setProperty('--color-primary-light',  lighten(primary, 0.88));
    root.style.setProperty('--color-primary-dark',   lighten(primary, 0.15));   // version foncée

    // Surface teintée : fond de la barre latérale aux couleurs de l'entité.
    //
    // 0.80 est un compromis mesuré, pas un réglage arbitraire. La barre compte
    // plus de deux cents libellés en gris foncé et près de trois cents fonds
    // de survol clairs : la couleur pleine les rendrait illisibles. À cette
    // dilution, la teinte de marque se voit franchement tout en laissant
    // passer le texte sombre.
    // Le fond prend la couleur de marque a pleine intensite : AGEROUTE veut son
    // orange, pas une trace d'orange. La dilution precedente (80 % de blanc)
    // lavait la teinte au point qu'elle passait pour un beige.
    root.style.setProperty('--color-primary-surface', primary);

    // Le texte de la barre suit le fond par defaut : on retient, du noir ou du
    // blanc, celui qui contraste le mieux avec la couleur de marque —
    // l'orange #E7530D appelle du quasi-noir (4,8:1), un bleu profond appellera
    // du blanc.
    //
    // Ce calcul garantit le contraste mais ignore la charte : sur un vert pale,
    // le quasi-noir rend un gris terne que personne n'aurait choisi. Une entite
    // peut donc IMPOSER sa couleur, qui l'emporte alors sur le calcul — et la
    // responsabilite de la lisibilite passe avec elle.
    const texteImpose = user?.couleurTexteMenu?.trim();
    const texteMenu = texteImpose || contrasteMax(primary);
    root.style.setProperty('--color-sidebar-text', texteMenu);

    // Les libelles secondaires (chevrons, compteurs) derivent de la couleur
    // retenue plutot que du fond : une couleur imposee doit entrainer toute la
    // barre avec elle, sinon le menu se retrouve a deux tons sans raison.
    root.style.setProperty(
      '--color-sidebar-muted',
      texteImpose
        ? attenuer(texteImpose, 0.62)
        : (estSombre(primary) ? 'rgba(255,255,255,.72)' : 'rgba(17,24,39,.62)'),
    );
    root.style.setProperty('--color-sidebar-hover', estSombre(primary) ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.55)');

    // Icones du menu. Par defaut elles suivent le texte : c'est le comportement
    // historique, et le changer d'office bouleverserait toutes les entites en
    // production. Une entite peut leur donner sa SECONDE couleur de charte —
    // beaucoup de logos en portent deux, et distinguer l'icone du libelle donne
    // du relief a la barre sans l'alourdir.
    //
    // La responsabilite du contraste passe avec le choix : une icone trop
    // proche du fond disparait, et aucun calcul ne peut deviner l'intention.
    root.style.setProperty(
      '--color-sidebar-icon',
      user?.couleurIconeMenu?.trim() || texteMenu,
    );

    // Bandeau supérieur : la couleur SECONDAIRE de l'entité.
    //
    // Beaucoup de logos portent deux couleurs — AGEROUTE associe un orange
    // #E7530D à un bleu nuit #1F1949. La primaire habille la barre latérale,
    // la secondaire le bandeau : les deux se retrouvent à l'écran.
    //
    // #6b7280 est le gris par défaut attribué aux entités qui n'ont jamais
    // choisi de secondaire. On le traite comme « non défini » et le bandeau
    // reste blanc, plutôt que d'imposer un bandeau gris à tout le monde.
    const secondaireChoisie = secondary && secondary.toLowerCase() !== '#6b7280';
    const fondEntete = secondaireChoisie ? secondary : '#ffffff';
    root.style.setProperty('--color-header', fondEntete);
    // Le texte suit la luminance du fond : inutile de deviner si la couleur
    // choisie sera claire ou sombre.
    root.style.setProperty('--color-header-text', estSombre(fondEntete) ? '#ffffff' : '#1f2937');
    root.style.setProperty('--color-header-muted', estSombre(fondEntete) ? '#cbd5e1' : '#6b7280');
    root.style.setProperty('--color-header-hover', estSombre(fondEntete) ? 'rgba(255,255,255,.12)' : 'rgba(0,0,0,.05)');

    root.style.setProperty('--color-secondary',      secondary);
    root.style.setProperty('--color-secondary-rgb',  hexToRgbParts(secondary));
    root.style.setProperty('--color-secondary-light',lighten(secondary, 0.88));

    root.style.setProperty('--color-accent',         accent);
    root.style.setProperty('--color-accent-rgb',     hexToRgbParts(accent));
    root.style.setProperty('--color-accent-light',   lighten(accent, 0.88));

    // Nettoyage : remettre les valeurs par défaut à la déconnexion
    return () => {
      root.style.setProperty('--color-primary',        DEFAULTS.primary);
      root.style.setProperty('--color-primary-rgb',    hexToRgbParts(DEFAULTS.primary));
      root.style.setProperty('--color-primary-light',  lighten(DEFAULTS.primary, 0.88));
      root.style.setProperty('--color-primary-dark',   lighten(DEFAULTS.primary, 0.15));
      root.style.setProperty('--color-secondary',      DEFAULTS.secondary);
      root.style.setProperty('--color-secondary-rgb',  hexToRgbParts(DEFAULTS.secondary));
      root.style.setProperty('--color-secondary-light',lighten(DEFAULTS.secondary, 0.88));
      root.style.setProperty('--color-accent',         DEFAULTS.accent);
      root.style.setProperty('--color-accent-rgb',     hexToRgbParts(DEFAULTS.accent));
      root.style.setProperty('--color-accent-light',   lighten(DEFAULTS.accent, 0.88));
    };
  }, [user?.couleurPrimaire, user?.couleurSecondaire, user?.couleurAccent, user?.couleurTexteMenu]);
}
